import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { Button, MenuItem, Intent } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { TimezonePicker } from '@blueprintjs/timezone';
import RichTextEditor from 'react-rte';
import _ from 'lodash';
import Toast from 'helpers/toaster';
import { FormSection, FieldError, Choice, Toggle } from 'components/FormBits';
import { SettingsCard } from 'components/SettingsPage';
import { MOBILE_PERMISSIONS, isOn } from 'components/User/EditUserPanel';

const toolbarConfig = {
  display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
  INLINE_STYLE_BUTTONS: [
    { label: 'Bold', style: 'BOLD', className: 'custom-css-class' },
    { label: 'Italic', style: 'ITALIC' },
    { label: 'Underline', style: 'UNDERLINE' },
  ],
  BLOCK_TYPE_DROPDOWN: [
    { label: 'Normal', style: 'unstyled' },
    { label: 'Heading Large', style: 'header-one' },
    { label: 'Heading Medium', style: 'header-two' },
    { label: 'Heading Small', style: 'header-three' },
  ],
  BLOCK_TYPE_BUTTONS: [
    { label: 'UL', style: 'unordered-list-item' },
    { label: 'OL', style: 'ordered-list-item' },
  ],
};

export const TEAM_SIZES = [
  { value: '1-5', label: '1–5 people' },
  { value: '5-25', label: '5–25 people' },
  { value: '25-50', label: '25–50 people' },
];

// Stored values are kept as they are (the server has them); labels are tidied.
export const COMPANY_TYPES = [
  ['Appliance Repair', 'Appliance repair'],
  ['Carpet Cleaning', 'Carpet cleaning'],
  ['Commercial Cleaning', 'Commercial cleaning'],
  ['Electrical Cleaning', 'Electrical cleaning'],
  ['General Contracting', 'General contracting'],
  ['HVAC', 'HVAC'],
  ['Landscaping', 'Landscaping'],
  ['Presure Washing', 'Pressure washing'],
  ['Residential Cleaning', 'Residential cleaning'],
  ['Pest Control', 'Pest control'],
  ['Plumbing', 'Plumbing'],
  ['Snow Removing', 'Snow removal'],
  ['Painting', 'Painting'],
  ['Others', 'Other'],
].map(([value, label]) => ({ value, label }));

const DISPATCH_VIEWS = [
  { value: 'timelineDay', label: 'Timeline' },
  { value: 'basicWeek', label: 'Week' },
];

// Shown on PDFs and public links, and other on/off settings for documents.
const DOCUMENT_TOGGLES = [
  { key: 'show_invoice_date_on_pdf', label: 'Show the invoice date on PDFs and public links' },
  { key: 'show_invoice_due_date_on_pdf', label: 'Show the due date on PDFs and public links' },
  { key: 'show_invoice_agreement_on_pdf', label: 'Show the invoice agreement on PDFs and public links' },
  { key: 'dont_show_pma_hours_on_work_reports', label: "Don't show PMA hours on work orders" },
];

const blank = (value) => value === undefined || value === null || String(value).trim() === '';

// Required fields -> [input id, message], in the order they appear.
export const REQUIRED = [
  ['name', 'company-name', 'Enter your company name.'],
  ['phone', 'company-phone', 'Enter a phone number.'],
  ['street_address', 'company-address-1', 'Enter the street address.'],
  ['city', 'company-city', 'Enter the city.'],
  ['province', 'company-province', 'Choose a state or province.'],
  ['currency', 'company-country', 'Choose a country. It sets your currency.'],
  ['timezone', 'company-timezone', 'Choose a time zone.'],
  ['team_size', 'company-team-size', 'Choose your team size.'],
  ['company_type', 'company-type', 'Choose your type of business.'],
];

const emptyAgreement = () => RichTextEditor.createValueFromString('<p><strong>  </strong></p>', 'html');

// Settings > Company details (pages/preferences/company_details.js). Edits the
// loaded tenant; Save sends it (preferenceStore.updateTenant).
@inject('preferenceStore', 'viewStore', 'quickbooksStore') @observer
class CompanyDetailsForm extends Component {
  @observable countryOptions = [];
  @observable stateOptions = [];
  @observable errors = {};
  @observable invoiceAgreement = emptyAgreement();
  @observable estimateAgreement = emptyAgreement();

  componentDidMount() {
    const { preferenceStore, viewStore } = this.props;
    const attributes = this.attributes;
    this.invoiceAgreement = attributes.invoice_agreement
      ? RichTextEditor.createValueFromString(attributes.invoice_agreement, 'html')
      : emptyAgreement();
    this.estimateAgreement = attributes.estimate_agreement
      ? RichTextEditor.createValueFromString(attributes.estimate_agreement, 'html')
      : emptyAgreement();
    viewStore.stateSearch.where.country_id = attributes.country_id || 233;
    preferenceStore.loadStates().then((options) => {
      if (options) this.stateOptions = options;
    }).catch(() => undefined);
    preferenceStore.loadCountries().then((options) => {
      if (options) this.countryOptions = options;
    }).catch(() => undefined);
  }

  get attributes() {
    return this.props.preferenceStore.tenant.object.attributes;
  }

  set(key, value) {
    this.attributes[key] = value;
    if (this.errors[key]) {
      this.errors = { ...this.errors, [key]: null };
    }
  }

  chooseCountry = (country) => {
    const { preferenceStore, viewStore } = this.props;
    const attributes = this.attributes;
    attributes.country = country.attributes.name;
    attributes.country_id = `${country.attributes.country_id}`;
    attributes.currency = country.attributes.currency_code;
    attributes.currency_symbol = country.attributes.currency_symbol;
    attributes.province = '';
    this.errors = { ...this.errors, currency: null };
    viewStore.stateSearch.where.country_id = country.attributes.country_id;
    preferenceStore.loadStates().then((options) => {
      this.stateOptions = options || [];
    }).catch(() => undefined);
  };

  filterCountries = (query) => {
    this.countryOptions = _.filter(this.props.preferenceStore.countries.objects, (option) =>
      option.attributes.name.toLowerCase().includes(query.toLowerCase()));
  };

  filterStates = (query) => {
    this.stateOptions = _.filter(this.props.preferenceStore.states.objects, (option) =>
      option.attributes.name.toLowerCase().includes(query.toLowerCase()));
  };

  changeLogo = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.attributes.logo = reader.result;
      this.attributes.logo_file = reader.result;
    };
    reader.readAsDataURL(file);
  };

  validate() {
    const attributes = this.attributes;
    const errors = {};
    REQUIRED.forEach(([key, , message]) => {
      if (blank(attributes[key])) errors[key] = message;
    });
    return errors;
  }

  save = () => {
    const { preferenceStore } = this.props;
    if (preferenceStore.isSavingTenant) return;
    const errors = this.validate();
    this.errors = errors;
    const first = REQUIRED.find(([key]) => errors[key]);
    if (first) {
      const element = document.getElementById(first[1]);
      if (element) {
        element.scrollIntoView({ block: 'center' });
        const target = element.matches('input, select, button') ? element : element.querySelector('input, select, button');
        if (target) target.focus();
      }
      return;
    }
    const attributes = this.attributes;
    // Defaults the booking form needs (as before).
    if (blank(attributes.country)) attributes.country = 'United States';
    if (blank(attributes.country_id)) attributes.country_id = '233';
    if (blank(attributes.heading_text_for_service_type)) attributes.heading_text_for_service_type = 'Choose Service Type';
    if (blank(attributes.another_heading_text_for_service)) attributes.another_heading_text_for_service = 'Choose Your Service Type';

    preferenceStore.updateTenant(preferenceStore.tenant.object)
      .then(() => {
        Toast.show({ message: 'Company details saved.', intent: Intent.SUCCESS });
      })
      .catch((error) => {
        const data = error && error.response && error.response.data;
        const reason = data && (data.error || data.errors);
        Toast.show({
          message: reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : "Couldn't save the company details. Try again.",
          intent: Intent.DANGER,
        });
      });
  };

  textField(key, label, { id, required, type = 'text', placeholder, autoComplete } = {}) {
    const fieldId = id || `company-${key.replace(/_/g, '-')}`;
    const error = this.errors[key];
    return (
      <div className={`bp3-form-group${error ? ' has-error' : ''}`}>
        <label className="fb-label" htmlFor={fieldId}>
          {label}
          {required && <span className="fb-required" aria-hidden="true"> *</span>}
        </label>
        <input
          id={fieldId}
          type={type}
          className="bp3-input bp3-fill"
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-required={required ? true : undefined}
          aria-invalid={error ? true : undefined}
          value={this.attributes[key] || ''}
          onChange={(event) => this.set(key, event.target.value)}
        />
        <FieldError>{error}</FieldError>
      </div>
    );
  }

  fieldLabel(label, htmlFor, required) {
    return (
      <label className="fb-label" htmlFor={htmlFor}>
        {label}
        {required && <span className="fb-required" aria-hidden="true"> *</span>}
      </label>
    );
  }

  renderCompany() {
    const attributes = this.attributes;
    return (
      <FormSection title="Company" hint="Your business name, logo and how customers reach you.">
        <div className="cd-logo">
          <span className="cd-logo__image">
            {attributes.logo
              ? <img src={attributes.logo} alt="Company logo" />
              : <span className="cd-logo__empty" aria-hidden="true">No logo</span>}
          </span>
          <label className="bp3-button sp-button cd-logo__change" htmlFor="company-logo">
            {attributes.logo ? 'Change logo' : 'Add logo'}
          </label>
          <input id="company-logo" className="sr-only" type="file" accept="image/*" onChange={this.changeLogo} />
        </div>
        {this.textField('name', 'Company name', { required: true, autoComplete: 'organization' })}
        <div className="fb-grid">
          {this.textField('phone', 'Phone', { required: true, type: 'tel', autoComplete: 'tel' })}
          {this.textField('website', 'Website', { type: 'url', placeholder: 'e.g. https://example.com', autoComplete: 'url' })}
        </div>
      </FormSection>
    );
  }

  renderAddress() {
    const { preferenceStore: { countries, states } } = this.props;
    const attributes = this.attributes;
    return (
      <FormSection title="Address" hint="Where your business is.">
        {this.textField('street_address', 'Address line 1', { id: 'company-address-1', required: true, autoComplete: 'address-line1' })}
        {this.textField('street_address_other', 'Address line 2', { id: 'company-address-2', autoComplete: 'address-line2' })}
        <div className="fb-grid">
          {this.textField('city', 'City', { required: true, autoComplete: 'address-level2' })}
          <div className={`bp3-form-group${this.errors.province ? ' has-error' : ''}`}>
            {this.fieldLabel('State or province', 'company-province', true)}
            <div className="bp3-form-content">
              <Select
                filterable={true}
                items={this.stateOptions}
                onQueryChange={this.filterStates}
                onItemSelect={(item) => this.set('province', item.attributes.name)}
                itemRenderer={(item, { handleClick, modifiers }) => (
                  <MenuItem key={item.attributes.name} text={item.attributes.name} active={modifiers.active} onClick={handleClick} />
                )}
                popoverProps={{ minimal: true }}
              >
                <Button
                  id="company-province"
                  rightIcon="caret-down"
                  text={attributes.province || 'Choose a state or province'}
                  className={attributes.province ? undefined : 'cd-placeholder'}
                  fill={true}
                  loading={states.isLoading}
                />
              </Select>
            </div>
            <FieldError>{this.errors.province}</FieldError>
          </div>
          <div className={`bp3-form-group${this.errors.currency ? ' has-error' : ''}`}>
            {this.fieldLabel('Country', 'company-country', true)}
            <div className="bp3-form-content">
              <Select
                filterable={true}
                items={this.countryOptions}
                onQueryChange={this.filterCountries}
                onItemSelect={this.chooseCountry}
                itemRenderer={(item, { handleClick, modifiers }) => (
                  <MenuItem key={item.attributes.name} text={item.attributes.name} active={modifiers.active} onClick={handleClick} />
                )}
                popoverProps={{ minimal: true }}
              >
                <Button
                  id="company-country"
                  rightIcon="caret-down"
                  text={attributes.country || 'United States'}
                  fill={true}
                  loading={countries.isLoading}
                />
              </Select>
            </div>
            <FieldError>{this.errors.currency}</FieldError>
          </div>
        </div>
      </FormSection>
    );
  }

  renderRegion() {
    const attributes = this.attributes;
    const currency = [attributes.currency, attributes.currency_symbol].filter(Boolean).join(' ');
    return (
      <FormSection title="Region" hint="Your time zone. The currency comes from your country.">
        <div className="fb-grid">
          <div className={`bp3-form-group${this.errors.timezone ? ' has-error' : ''}`}>
            {this.fieldLabel('Time zone', 'company-timezone', true)}
            <div className="bp3-form-content">
              <TimezonePicker
                value={attributes.timezone}
                onChange={(timezone) => this.set('timezone', timezone)}
                valueDisplayFormat="composite"
                buttonProps={{ id: 'company-timezone', fill: true }}
              />
            </div>
            <FieldError>{this.errors.timezone}</FieldError>
          </div>
          <div className="bp3-form-group">
            <span className="fb-label">Currency</span>
            <p className="cd-readonly">{currency || 'Set when you choose a country'}</p>
          </div>
        </div>
      </FormSection>
    );
  }

  renderBusiness() {
    const attributes = this.attributes;
    return (
      <FormSection title="Your business" hint="The size of your team and the work you do.">
        <div id="company-team-size">
          <Choice
            label="Team size"
            name="Team size"
            required={true}
            value={attributes.team_size || ''}
            onChange={(value) => this.set('team_size', value)}
            options={TEAM_SIZES}
            error={this.errors.team_size}
          />
        </div>
        <div className={`bp3-form-group${this.errors.company_type ? ' has-error' : ''}`}>
          {this.fieldLabel('Type of business', 'company-type', true)}
          <div className="bp3-html-select bp3-fill">
            <select
              id="company-type"
              value={attributes.company_type || ''}
              onChange={(event) => this.set('company_type', event.target.value)}
            >
              <option value="" disabled>Choose your type of business</option>
              {COMPANY_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </div>
          <FieldError>{this.errors.company_type}</FieldError>
        </div>
      </FormSection>
    );
  }

  renderDocuments() {
    const attributes = this.attributes;
    return (
      <FormSection title="Estimates and invoices" hint="Your tax number, and what shows on documents.">
        <div className="fb-grid">
          {this.textField('tax_number_title', 'Tax number label', { placeholder: 'e.g. HST#' })}
          {this.textField('tax_number', 'Tax number', { placeholder: 'e.g. 123-212-212-RT01' })}
        </div>
        {DOCUMENT_TOGGLES.map(({ key, label }) => (
          <Toggle
            key={key}
            id={`company-${key.replace(/_/g, '-')}`}
            label={label}
            checked={isOn(attributes[key])}
            onChange={(checked) => this.set(key, checked)}
          />
        ))}
      </FormSection>
    );
  }

  renderAgreements() {
    return (
      <FormSection title="Agreements" hint="Terms that go with your estimates and invoices.">
        <div className="bp3-form-group cd-editor">
          <span className="fb-label">Estimate agreement</span>
          <RichTextEditor
            value={this.estimateAgreement}
            onChange={(value) => { this.estimateAgreement = value; this.attributes.estimate_agreement = value.toString('html'); }}
            className="text-editor"
            toolbarConfig={toolbarConfig}
            placeholder="Estimate agreement"
          />
        </div>
        <div className="bp3-form-group cd-editor">
          <span className="fb-label">Invoice agreement</span>
          <RichTextEditor
            value={this.invoiceAgreement}
            onChange={(value) => { this.invoiceAgreement = value; this.attributes.invoice_agreement = value.toString('html'); }}
            className="text-editor"
            toolbarConfig={toolbarConfig}
            placeholder="Invoice agreement"
          />
        </div>
      </FormSection>
    );
  }

  renderMobile() {
    const attributes = this.attributes;
    return (
      <FormSection title="Mobile app" hint="What your team can do in the Field Nexus app.">
        {MOBILE_PERMISSIONS.map(({ key, label }) => (
          <Toggle
            key={key}
            id={`company-${key.replace(/_/g, '-')}`}
            label={label}
            checked={isOn(attributes[key])}
            onChange={(checked) => this.set(key, checked)}
          />
        ))}
      </FormSection>
    );
  }

  renderEmailAndDispatch() {
    const attributes = this.attributes;
    return (
      <FormSection title="Email and dispatch" hint="Where customer replies go, and how the dispatch board opens.">
        {this.textField('reply_to', 'Reply-to email', { type: 'email', placeholder: 'e.g. office@example.com', autoComplete: 'email' })}
        <Choice
          label="Dispatch board opens on"
          name="Dispatch board default view"
          value={attributes.dispatch_board_default_view || ''}
          onChange={(value) => this.set('dispatch_board_default_view', value)}
          options={DISPATCH_VIEWS}
        />
      </FormSection>
    );
  }

  renderQuickbooks() {
    const items = this.props.quickbooksStore.quickbooksItems.objects;
    if (!items || items.length === 0) return null;
    return (
      <FormSection title="QuickBooks" hint="The product or service to use by default.">
        <div className="bp3-form-group">
          {this.fieldLabel('Default product or service', 'quickbooks-product-service')}
          <div className="bp3-html-select bp3-fill">
            <select
              id="quickbooks-product-service"
              value={this.attributes.default_quickbooks_product_sercives_id || ''}
              onChange={(event) => this.set('default_quickbooks_product_sercives_id', event.target.value)}
            >
              <option value="">None</option>
              {items.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
            </select>
          </div>
        </div>
      </FormSection>
    );
  }

  render() {
    const saving = this.props.preferenceStore.isSavingTenant;
    return (
      <SettingsCard
        className="cd"
        footer={<Button className="sp-button sp-button--primary" icon="floppy-disk" text="Save changes" loading={saving} onClick={this.save} />}
      >
        {this.renderCompany()}
        {this.renderAddress()}
        {this.renderRegion()}
        {this.renderBusiness()}
        {this.renderDocuments()}
        {this.renderAgreements()}
        {this.renderMobile()}
        {this.renderEmailAndDispatch()}
        {this.renderQuickbooks()}
      </SettingsCard>
    );
  }
}

export default CompanyDetailsForm;
